<template>
  <SsrJobLinks :links="jobLinks" />

  <ClientOnly>
    <template #fallback>
      <CRSearchLoadingPlaceholder :job-search-global="jobSearchGlobal" />
    </template>

    <AisInstantSearch
      v-if="searchAvailable"
      :search-client="searchClient"
      :routing="routing"
      :index-name="indexName"
      :insights="{
        insightsClient: null,
        onEvent: (event: any) => {
          handleTrackInsightsEvent(event);
        },

      }"
    >
      <CRSearchFilterPageTemplate
        :computed-function="computed"
        :job-search-global="jobSearchGlobal"
        :search-tablist-aria-label="searchTablistAriaLabel"
        :toggle-list-view-aria-label="searchToggleListViewAriaLabel"
        :toggle-map-view-aria-label="searchToggleMapViewAriaLabel"
        :save-filters-text="searchSaveFiltersText"
        @filter-clicked="handleTrackFilterOpened($event)"
        @view-changed="handleViewChanged"
      >
        <template #search-box>
          <AisSearchBox
            :placeholder="jobSearchGlobal.config.search.placeholderText"
            @input="handleTrackSearchInput"
          />
        </template>

        <template #filter-list="{ filter }">
          <CRListFilterWrapper :filter-key="filter.key">
            <AisRefinementList :attribute="filter.key" searchable :limit="1000" :sort-by="listSorting">
              <template
                #default="{
                  items,
                  isFromSearch,
                  refine,
                  createURL,
                  searchForItems,
                  sendEvent,
                }"
              >
                <CREndlessFilterList
                  :attribute="filter.key"
                  :items="items"
                  :refine="refine"
                  :search-for-items="searchForItems"
                  :create-u-r-l="createURL"
                  :is-from-search="isFromSearch"
                  :send-event="sendEvent"
                  :reset-button-label="searchClearFilterSearchLabel"
                  :job-search-global="jobSearchGlobal"
                />
              </template>
            </AisRefinementList>
          </CRListFilterWrapper>
        </template>

        <template #filter-location="{ filter }">
          <CRListFilterWrapper :filter-key="filter.key">
            <div>
              <!-- eslint-disable vue/no-unused-refs -->
              <CRLocationAutocomplete
                ref="locationAutocomplete"
                :api-url="locationAutocompleteUrl"
                :language="jobShopLocale.split('-')[0]"
                :placeholder="jobSearchGlobal.config.filters.locationSearchText"
                :clear-input="resetAutocomplete"
                :clear-button-label="searchClearLocationLabel"
                :initial-value="locationLabel"
                @address-chosen="handleFilterAddress($event)"
                @address-cleared="handleClearAddress"
              />
              <!-- eslint-enable vue/no-unused-refs -->
              <CRLocationRadiusSelector
                :coordinates="aroundLatLng"
                :initial-radius="aroundRadius"
                :radius-options="[10000, 25000, 50000, 100000]"
                :group-label="searchRadiusGroupLabel"
                @radius-changed="handleChangeRadius($event)"
              />
              <AisConfigure
                v-if="aroundLatLng && aroundRadius"
                :filters.camel="coordinateFilter"
              />
            </div>
            <AisRefinementList
              :attribute="filter.key"
              searchable
              :limit="1000"
              :sort-by="listSorting"
            >
              <template
                #default="{
                  items,
                  isFromSearch,
                  refine,
                  createURL,
                  searchForItems,
                  sendEvent,
                }"
              >
                <CREndlessFilterList
                  :attribute="filter.key"
                  :items="items"
                  :refine="refine"
                  :search-for-items="searchForItems"
                  :create-u-r-l="createURL"
                  :is-from-search="isFromSearch"
                  :send-event="sendEvent"
                  :reset-button-label="searchClearFilterSearchLabel"
                  :job-search-global="jobSearchGlobal"
                  :hide-search="true"
                />
              </template>
            </AisRefinementList>
          </CRListFilterWrapper>
        </template>

        <template #filter-select="{ filter }">
          <AisRefinementList
            :attribute="filter.key"
            :limit="1000"
            :class-names="{
              'ais-RefinementList': 'search-filter-filter-select-list',
            }"
            :sort-by="listSorting"
          >
            <template #default="{ items, refine }">
              <button
                v-for="item in items.sort((a, b) => a.value < b.value)"
                :key="item.value"
                class="select-button"
                :class="item.isRefined ? '-active' : ''"
                @click.prevent="refine(item.value)"
              >
                {{ item.value }}
              </button>
            </template>
          </AisRefinementList>
        </template>

        <template #filter-slider="{ filter }">
          <AisRangeInput :attribute="filter.key">
            <template #default="{ currentRefinement, range, refine }">
              <div :ref="`slider-${filter.key}`" class="slider-container">
                <Slider
                  :min="range.min"
                  :max="range.max"
                  :model-value="toValue(currentRefinement, range)"
                  :tooltips="true"
                  tooltip-position="bottom"
                  :options="sliderHandleOptions(filter)"
                  @update="refineRange(refine, $event)"
                  @slide="
                    setSliderTooltipOrientation($event, range, filter.key)
                  "
                />
              </div>
            </template>
          </AisRangeInput>
        </template>

        <template #single-filter-reset="{ filter }">
          <AisClearRefinements :included-attributes="[filter.key]">
            <template #resetLabel>
              {{ jobSearchGlobal.config.filters.resetButtonsText.singleFilter }}
            </template>
          </AisClearRefinements>
        </template>

        <template #stats>
          <AisStats>
            <template #default="{ nbHits }">
              <!-- eslint-disable vue/no-unused-refs -->
              <CRSearchStats ref="searchStats" :number-of-hits="nbHits" :stats-text="jobSearchGlobal.config.results.amountHitsText" :total-count="totalOffersCount" />
              <!-- eslint-enable vue/no-unused-refs -->
            </template>
          </AisStats>
        </template>

        <template #sorting-select>
          <AisSortBy :items="jobSearchGlobal.config.sorting.items" />
        </template>

        <template #all-filter-reset>
          <AisClearRefinements>
            <template #default="{ canRefine, refine }">
              <CRResetFiltersButton
                :label="
                  jobSearchGlobal.config.filters.resetButtonsText.allFilters
                "
                :disabled="!canRefine && !(aroundRadius && aroundLatLng)"
                @click.prevent="handleClickRemoveAllFilter(refine)"
                @mousedown="handleTrackClickRemoveAllFilter"
              />
            </template>
          </AisClearRefinements>
        </template>

        <template #results>
          <AisInfiniteHits>
            <template #item="{ item, index }">
              <IntersectWrapper @intersects="handleTrackViewSearchResult(item, index)">
                <CRResultItem :item="item" :job-search-global="jobSearchGlobal" :data-id="item.id" :more-locations-text="moreLocationsText" />
              </IntersectWrapper>
            </template>

            <template #loadMore="{ isLastPage, refineNext }">
              <CRInfiniteScrollTrigger
                :is-last-page="isLastPage"
                @trigger-intersected="refineNext"
              />
            </template>
          </AisInfiniteHits>
        </template>

        <template #empty-state>
          <AisStateResults>
            <template #default="{ results: { hits } }">
              <CRResultsEmptyState
                v-show="hits.length === 0"
                :headline="jobSearchGlobal.config.results.emptyState.headline"
                :subline="jobSearchGlobal.config.results.emptyState.subline"
              />
            </template>
          </AisStateResults>
        </template>

        <template #map-view>
          <AisConfigure :hits-per-page.camel="250" />
          <CRSearchMapContainer
            :job-search-global="jobSearchGlobal"
            :tiles-url="mapTilesUrl"
            :cdn-url="cdnUrl"
            :map-language="jobShopLocale.split('-')[0]"
          >
            <CRSearchMapVue3
              @marker-clicked="handleTrackMapMarkerClicked"
              @slide-changed="handleTrackMapMarkerSlideChanged"
            />
          </CRSearchMapContainer>
        </template>
      </CRSearchFilterPageTemplate>
    </AisInstantSearch>

    <div v-else class="search-unavailable">
      {{ searchUnavailableText }}
    </div>
  </ClientOnly>
</template>

<script lang="ts">
import {
  AisClearRefinements,
  AisConfigure,
  AisInfiniteHits,
  AisInstantSearch,
  AisRangeInput,
  AisRefinementList,
  AisSearchBox,
  AisSortBy,
  AisStateResults,
  AisStats,
} from "vue-instantsearch/vue3/es"
import Slider from "@vueform/slider"
import { searchAndFilterMethods } from "@jobshop/crossroads/components/SearchAndFilter/mixins/searchAndFilter"
import { debounce } from "radash"
import { useTypesenseSearchClient } from "~/composables/useTypesenseSearchClient"
import { InsightsEvents } from "~/utils/enums/InsightsEvents"

const indexName = "offers"
let activeFilters = []

export default defineNuxtComponent({
  components: {
    AisClearRefinements,
    AisInfiniteHits,
    AisInstantSearch,
    AisRangeInput,
    AisRefinementList,
    AisSearchBox,
    AisSortBy,
    AisStateResults,
    AisStats,
    AisConfigure,
    Slider,
  },
  mixins: [searchAndFilterMethods],
  async setup() {
    const { t } = useI18n()

    // Provide search config
    const { data: jobShop } = useNuxtData("jobShopData")
    const jobSearchGlobal = ref(jobShop.value?.search)

    // Provide locale & cdn urls for map
    const jobShopLocale = ref(jobShop.value?.locale)

    const { moreLocationsText } = useMoreLocationsText(jobShopLocale, t)
    const { public: { cdnUrl, mapTilesUrl, locationAutocompleteUrl, typesenseUrl } } = useRuntimeConfig()

    // Resolve the router and the useSsrJobLinks dependencies before any await —
    // hooks relying on getCurrentInstance() must not be called after an await in
    // async setup() (see AGENTS.md).
    const router = useRouter()
    const nuxtApp = useNuxtApp()
    const { tenantId, jobShopId } = useHeaderStore()

    // Provide translations
    const searchTablistAriaLabel = t("search.tablist-aria-label", 0, { locale: jobShopLocale.value })
    const searchToggleMapViewAriaLabel = t("search.toggle-map-view-aria-label", 0, { locale: jobShopLocale.value })
    const searchToggleListViewAriaLabel = t("search.toggle-list-view-aria-label", 0, { locale: jobShopLocale.value })
    const searchSaveFiltersText = t("search.save-filters", 0, { locale: jobShopLocale.value })
    const searchClearLocationLabel = t("search.clear-location", 0, { locale: jobShopLocale.value })
    const searchRadiusGroupLabel = t("search.radius-group", 0, { locale: jobShopLocale.value })
    const searchClearFilterSearchLabel = t("search.clear-filter-search", 0, { locale: jobShopLocale.value })
    const sliderHandleOptionsCache = new Map<string, object>()
    const sliderHandleOptions = (filter: { label?: string }) => {
      const locale = jobShopLocale.value
      const cacheKey = `${locale}|${filter.label ?? ""}`
      if (!sliderHandleOptionsCache.has(cacheKey)) {
        sliderHandleOptionsCache.set(cacheKey, {
          handleAttributes: [
            { "aria-label": filter.label ? t("search.slider-min-of", { label: filter.label }, { locale }) : t("search.slider-min", 0, { locale }) },
            { "aria-label": filter.label ? t("search.slider-max-of", { label: filter.label }, { locale }) : t("search.slider-max", 0, { locale }) },
          ],
        })
      }
      return sliderHandleOptionsCache.get(cacheKey)
    }

    // Fetch typesense API key with target job shops
    const targetJobShops = jobSearchGlobal.value?.config.general.targetJobShops
    const { typesenseApiKey, refreshTypesenseApiKey } = await useTypesenseApiKey(targetJobShops)

    // Search is available only when the scoped key resolved (offer-service reachable);
    // flipped to false if a search later fails at runtime.
    const searchAvailable = ref(!!typesenseApiKey.value)
    const searchUnavailableText = t("search.unavailable", 0, { locale: jobShopLocale.value })

    // Get typesense search client (skip entirely when there is no key)
    let searchClient = null
    let routing = null

    if (searchAvailable.value) {
      const client = await useTypesenseSearchClient(jobSearchGlobal.value?.config, typesenseApiKey.value, refreshTypesenseApiKey)
      searchClient = client.searchClient

      // read active filters for usage in routing
      activeFilters = jobSearchGlobal.value?.config.filters.options.filter((filter) => {
        return filter.active
      })

      // setup routing
      routing = {
        router: useNuxtInstantsearchRouter(router, activeFilters),
      }
    }

    // Crawlable (no-JS) job links rendered into a <noscript> block — see SsrJobLinks.vue.
    // The search page has no presets, so it links the full offer set. (JET-1826)
    const { jobLinks } = await useSsrJobLinks(
      typesenseApiKey.value,
      { api: nuxtApp.$api, tenantId, jobShopId, typesenseUrl },
      { scope: "search" },
    )

    // Track loaded filters
    // Only possible on client, since amplitude is a client-side plugin
    if (import.meta.client) {
      jobSearchGlobal.value?.config.filters.options.forEach((filter: FilterOption) => {
        if (filter.active)
          trackFilterLoaded(filter.key)
      })
    }

    // Use ref on CRSearchStats to track number of results
    const searchStats = ref(null)
    watchEffect(() => {
      if (searchStats.value)
        trackLoadSearchResults(searchStats.value.numberOfHits)
    })

    // Define sorting for list and select filters
    const listSorting = ref(["isRefined", "name:asc"])

    // Get total offers
    const totalOffersCount = ref(0)
    const fetchTotalOffersCount = async () => {
      if (!searchAvailable.value || !searchClient)
        return

      try {
        const response = await searchClient.value.search([
          {
            indexName: "offers",
            params: {
              query: "",
            },
          },
        ])

        totalOffersCount.value = response.results[0].nbHits
      }
      catch {
        searchAvailable.value = false
      }
    }

    return {
      routing,
      jobLinks,
      jobSearchGlobal,
      jobShopLocale,
      moreLocationsText,
      cdnUrl,
      mapTilesUrl,
      searchClient,
      searchStats,
      indexName,
      listSorting,
      totalOffersCount,
      fetchTotalOffersCount,
      locationAutocompleteUrl,
      searchTablistAriaLabel,
      searchToggleMapViewAriaLabel,
      searchToggleListViewAriaLabel,
      searchAvailable,
      searchUnavailableText,
      searchSaveFiltersText,
      searchClearLocationLabel,
      searchRadiusGroupLabel,
      searchClearFilterSearchLabel,
      sliderHandleOptions,
    }
  },
  mounted() {
    this.fetchTotalOffersCount()
  },
  methods: {
    isGeoUrlSyncEnabled() {
      return true
    },
    handleTrackSearchInput: debounce({ delay: 1000 }, (event) => {
      const searchRequest = event.target.value

      if (searchRequest)
        trackSearchApplied(searchRequest)
    }),
    handleTrackFilterOpened(event: string) {
      trackFilterOpened(event)
    },
    handleTrackClickRemoveAllFilter() {
      trackClickRemoveAllFilter()
    },
    handleClickRemoveAllFilter(refine) {
      this.resetAllFilters(refine)
      this.currentAddress = ""
    },
    handleViewChanged(event: any) {
      this.updateSearchParameters(event)
      trackSearchViewChanged(event)
    },
    handleTrackMapMarkerClicked() {
      trackClickMapMarker()
    },
    handleTrackMapMarkerSlideChanged(event: any) {
      trackMapMarkerSlideChanged(event)
    },
    handleTrackViewSearchResult(item: any, itemIndex: number) {
      trackViewSearchResult(item.id, item.title, itemIndex + 1)
    },
    handleTrackInsightsEvent(event: any) {
      switch (event.insightsMethod) {
        case InsightsEvents.CLICKED_FILTERS:
          trackFilterSelected(event.attribute, event.payload.filters[0].split(":")[1])
          break
        case InsightsEvents.CLICKED_RESULTS:
          trackClickSearchResult(event.hits[0].id, event.hits[0].title, event.payload.positions[0])
          break
      }
    },
    handleFilterAddress(address) {
      this.currentAddress = address
      this.filterAddress(address)

      if (this.currentAddress && this.aroundRadius)
        trackLocationRadiusFilterSelected(this.currentAddress.location.city, this.aroundRadius / 1000)
    },
    handleChangeRadius(km) {
      this.changeRadius(km)

      if (this.currentAddress && this.aroundRadius)
        trackLocationRadiusFilterSelected(this.currentAddress.location.city, this.aroundRadius / 1000)
    },
    handleClearAddress() {
      this.currentAddress = ""
      this.clearAddress()
    },
  },
})
</script>

<style src="@vueform/slider/themes/default.css"></style>

<style src="@jobshop/crossroads/assets/scss/search-filter/_index.scss"></style>

<style lang="scss" scoped>
.search-unavailable {
  @include font-family("body");
  @include scale-fonts("body");

  display: flex;
  align-items: center;
  justify-content: center;

  min-height: 240px;
  padding: var(--cr-content-padding-m, 24px);

  color: var(--search-filter-text-secondary-color, #5e5e5e);
  text-align: center;
  text-wrap: balance;
}
</style>
